import { Link } from "react-router-dom";
import {
  TreePine, Scissors, Leaf, Snowflake, Sprout, PaintBucket,
  Shield, Layers, ChevronRight, Star, Phone, ArrowRight, CheckCircle2
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { GoogleReviewsBadge, WriteReviewButton } from "@/components/GoogleReviewsBadge";

const HERO_IMG = "https://images.unsplash.com/photo-1761637822930-fb1c1a3df94d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const ABOUT_IMG1 = "https://images.pexels.com/photos/6728919/pexels-photo-6728919.jpeg?auto=compress&cs=tinysrgb&w=800";
const ABOUT_IMG2 = "https://images.unsplash.com/photo-1763025589551-06156a1ab9e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

const stats = [
  { value: "500+", label: "Projects Completed" },
  { value: "10+", label: "Years Experience" },
  { value: "100%", label: "Satisfaction Rate" },
  { value: "15+", label: "Areas Served" },
];

const topServices = [
  {
    icon: TreePine,
    title: "Landscaping Design",
    desc: "Complete landscape transformations — from concept to creation for your residential property.",
    color: "#3a5c2e",
  },
  {
    icon: Scissors,
    title: "Lawn Care & Cutting",
    desc: "Regular mowing, trimming, and edging to keep your lawn perfectly manicured.",
    color: "#3a5c2e",
  },
  {
    icon: Snowflake,
    title: "Snow Removal & Salting",
    desc: "Reliable snow clearing and ice management all winter long — driveways, walkways & more.",
    color: "#3a5c2e",
  },
  {
    icon: Sprout,
    title: "Planting & Mulching",
    desc: "Expert planting, mulching and fertilization to keep your garden beds healthy and vibrant.",
    color: "#3a5c2e",
  },
  {
    icon: Leaf,
    title: "Seasonal Cleanup",
    desc: "Spring and fall cleanup services — leaf removal, debris clearing and garden prep.",
    color: "#3a5c2e",
  },
  {
    icon: PaintBucket,
    title: "Fence & Deck Services",
    desc: "Fence staining, deck cleaning and driveway sealing to protect and refresh your property.",
    color: "#3a5c2e",
  },
];

const galleryImages = [
  {
    url: "https://images.unsplash.com/photo-1762810981576-1b07f76af9d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    alt: "Modern house with landscaped driveway",
  },
  {
    url: "https://images.pexels.com/photos/6728919/pexels-photo-6728919.jpeg?auto=compress&cs=tinysrgb&w=600",
    alt: "Lawn mowing service",
  },
  {
    url: "https://images.unsplash.com/photo-1768699107110-262fafb19b3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    alt: "Snow removal service",
  },
  {
    url: "https://images.unsplash.com/photo-1772040942277-b194d9d0b648?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    alt: "Garden deck area",
  },
  {
    url: "https://images.unsplash.com/photo-1759355787118-7b48305965d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    alt: "Manicured lawn and hedges",
  },
];

const testimonials = [
  {
    name: "Sarah Mitchell",
    location: "Mississauga, ON",
    text: "Absolute Landscapers completely transformed our backyard. The attention to detail and quality of work exceeded our expectations. Highly recommend for anyone in Mississauga!",
    rating: 5,
  },
  {
    name: "James Thornton",
    location: "Brampton, ON",
    text: "We've been using their snow removal service for two winters now. Always on time, thorough, and our driveway is always clear. Great value for the peace of mind.",
    rating: 5,
  },
  {
    name: "Priya Sharma",
    location: "Oakville, ON",
    text: "From the initial quote to the final result, everything was professional. Our front lawn looks incredible. The team is friendly, punctual, and takes real pride in their work.",
    rating: 5,
  },
];

const faqs = [
  {
    q: "What areas do you serve?",
    a: "We serve Mississauga and surrounding areas including Brampton, Oakville, Milton, Etobicoke, and the broader Greater Toronto Area (GTA).",
  },
  {
    q: "Do you offer free estimates?",
    a: "Yes! We offer free, no-obligation estimates for all our services. Simply fill out our contact form or call us directly to schedule your free assessment.",
  },
  {
    q: "What landscaping services do you provide?",
    a: "We offer a comprehensive range of services: lawn cutting, edging, trimming, fertilization, mulching, planting, sod installation, seasonal cleanup, snow removal, salting, fence staining, deck cleaning, and driveway sealing.",
  },
  {
    q: "Are you insured?",
    a: "Yes, Absolute Landscapers is fully insured. We carry liability insurance to protect both our team and your property during all work.",
  },
  {
    q: "How do you handle snow removal during storms?",
    a: "We monitor weather forecasts closely and dispatch our teams proactively during snowfall events. We prioritize our contracted clients to ensure driveways and walkways are cleared promptly and safely.",
  },
  {
    q: "Do you offer maintenance packages?",
    a: "Absolutely! We offer customizable seasonal maintenance packages that bundle services like regular lawn care, edging, fertilization, and seasonal cleanup at a discounted rate.",
  },
];

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${HERO_IMG}')` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-24 pt-32">
          <span
            className="inline-block mb-4 px-4 py-1.5 bg-[#8dc63f]/20 text-[#8dc63f] text-xs font-semibold rounded-full uppercase tracking-widest border border-[#8dc63f]/30"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Mississauga & GTA
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
            style={{ fontFamily: "'Outfit', sans-serif", textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}
          >
            Mississauga's Trusted
            <br />
            <span className="text-[#8dc63f]">Landscaping Experts</span>
          </h1>
          <p
            className="text-base sm:text-lg text-white/85 max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Professional residential landscaping, lawn care, snow removal and more. Serving Mississauga and surrounding GTA communities with pride.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="hero-cta-quote"
              className="btn-primary px-8 py-4 rounded-xl text-base inline-flex items-center gap-2 justify-center"
            >
              Get a Free Quote <ArrowRight size={18} />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="px-8 py-4 rounded-xl text-base border-2 border-white text-white font-semibold hover:bg-white hover:text-[#3a5c2e] transition-all inline-flex items-center gap-2 justify-center"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Our Services <ChevronRight size={18} />
            </Link>
          </div>
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 text-white/80 text-sm">
            <span className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-[#8dc63f]" /> Licensed & Insured
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-[#8dc63f]" /> Free Estimates
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-[#8dc63f]" /> 10+ Years Experience
            </span>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="bg-[#3a5c2e] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center text-white">
            {stats.map((s) => (
              <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="py-4">
                <div
                  className="text-4xl sm:text-5xl font-bold text-[#8dc63f] mb-2"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {s.value}
                </div>
                <div className="text-white/80 text-sm font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section data-testid="services-overview-section" className="py-20 md:py-28 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span
              className="uppercase tracking-[0.2em] text-xs font-semibold text-[#3a5c2e] block mb-3"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              What We Do
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1F291E] mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Comprehensive Landscaping Services
            </h2>
            <p className="text-[#4A5D48] text-base max-w-2xl mx-auto">
              From spring lawn care to winter snow removal, we handle every aspect of your outdoor property maintenance.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topServices.map((svc) => (
              <div
                key={svc.title}
                data-testid={`service-card-${svc.title.toLowerCase().replace(/\s/g, "-")}`}
                className="service-card bg-white border border-[#E2E8DF] rounded-xl p-7 transition-all duration-300 cursor-default"
              >
                <div className="w-12 h-12 rounded-xl bg-[#F0F4EF] flex items-center justify-center mb-5">
                  <svc.icon size={24} color={svc.color} strokeWidth={1.8} />
                </div>
                <h3
                  className="font-semibold text-lg text-[#1F291E] mb-2"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {svc.title}
                </h3>
                <p className="text-[#4A5D48] text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="btn-primary px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2"
            >
              View All 14 Services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT SNIPPET */}
      <section data-testid="about-snippet-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text side */}
            <div>
              <span
                className="uppercase tracking-[0.2em] text-xs font-semibold text-[#3a5c2e] block mb-3"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                About Us
              </span>
              <h2
                className="text-3xl sm:text-4xl font-bold text-[#1F291E] mb-6 leading-snug"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                We Are Mississauga's
                <span className="text-[#3a5c2e]"> Premier Landscaping</span> Service
              </h2>
              <p className="text-[#4A5D48] text-base leading-relaxed mb-5">
                With over a decade of experience serving residential clients in Mississauga and the Greater Toronto Area, Absolute Landscapers brings expert craftsmanship and genuine care to every project.
              </p>
              <p className="text-[#4A5D48] text-base leading-relaxed mb-8">
                Led by General Manager <strong className="text-[#1F291E]">Ahmad Parwiz</strong>, our dedicated team delivers exceptional results — from routine lawn maintenance to complete landscape transformations.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Licensed & fully insured for your peace of mind",
                  "Personalized service tailored to your property",
                  "Competitive pricing with no hidden fees",
                  "Prompt, reliable and professional team",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#4A5D48] text-sm">
                    <CheckCircle2 size={18} className="text-[#8dc63f] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                data-testid="about-learn-more-btn"
                className="btn-primary px-7 py-3 rounded-xl text-sm inline-flex items-center gap-2"
              >
                More About Us <ArrowRight size={16} />
              </Link>
            </div>
            {/* Image side */}
            <div className="relative">
              <div className="relative z-10">
                <img
                  src={ABOUT_IMG1}
                  alt="Professional lawn mowing"
                  className="rounded-2xl w-full h-[380px] object-cover shadow-xl"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 z-20 w-48 h-48 hidden md:block">
                <img
                  src={ABOUT_IMG2}
                  alt="Garden planting"
                  className="rounded-2xl w-full h-full object-cover shadow-xl border-4 border-white"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#8dc63f] rounded-full opacity-15 hidden md:block" />
              <div className="absolute top-1/2 -left-3 w-6 h-6 bg-[#3a5c2e] rounded-full opacity-40 hidden md:block" />
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section data-testid="gallery-preview-section" className="py-20 md:py-28 bg-[#F0F4EF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span
              className="uppercase tracking-[0.2em] text-xs font-semibold text-[#3a5c2e] block mb-3"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Our Work
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1F291E] mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Gallery of Transformations
            </h2>
            <p className="text-[#4A5D48] text-base max-w-xl mx-auto">
              See the quality of our work — from lush lawns to pristine driveways and everything in between.
            </p>
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {galleryImages.map((img, idx) => (
              <div
                key={idx}
                data-testid={`gallery-preview-img-${idx}`}
                className="gallery-item break-inside-avoid overflow-hidden rounded-xl"
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  className="w-full h-auto object-cover rounded-xl"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/gallery"
              data-testid="view-gallery-btn"
              className="btn-primary px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2"
            >
              View Full Gallery <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span
              className="uppercase tracking-[0.2em] text-xs font-semibold text-[#3a5c2e] block mb-3"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Testimonials
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1F291E]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <div
                key={idx}
                data-testid={`testimonial-${idx}`}
                className="bg-[#F9F8F6] border border-[#E2E8DF] rounded-xl p-7"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={16} fill="#8dc63f" color="#8dc63f" />
                  ))}
                </div>
                <p className="text-[#4A5D48] text-sm leading-relaxed mb-5 italic">
                  "{t.text}"
                </p>
                <div className="border-t border-[#E2E8DF] pt-4">
                  <p
                    className="font-semibold text-[#1F291E] text-sm"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {t.name}
                  </p>
                  <p className="text-[#4A5D48] text-xs">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS BAND */}
      <section data-testid="google-reviews-section" className="py-12 bg-white border-y border-[#E2E8DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <p
                className="text-[#1F291E] font-semibold text-base mb-1"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Our clients love us on Google
              </p>
              <p className="text-[#4A5D48] text-sm">
                Trusted by homeowners across Mississauga & the GTA
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <GoogleReviewsBadge />
              <WriteReviewButton />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-20 md:py-28 bg-[#F0F4EF]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span
              className="uppercase tracking-[0.2em] text-xs font-semibold text-[#3a5c2e] block mb-3"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              FAQ
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1F291E]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Your Questions Answered
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, idx) => (
              <AccordionItem
                key={idx}
                value={`faq-${idx}`}
                data-testid={`faq-item-${idx}`}
                className="bg-white border border-[#E2E8DF] rounded-xl px-2 overflow-hidden"
              >
                <AccordionTrigger
                  className="px-4 py-4 text-[#1F291E] font-medium text-sm hover:no-underline hover:text-[#3a5c2e]"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 text-[#4A5D48] text-sm leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA BANNER */}
      <section
        data-testid="cta-banner-section"
        className="relative py-20 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #3a5c2e 0%, #2d4a24 100%)" }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#8dc63f] rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#8dc63f] rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Ready to Transform Your Outdoor Space?
          </h2>
          <p className="text-white/80 text-base mb-8 max-w-xl mx-auto">
            Contact us today for a free, no-obligation estimate. Let us bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-get-quote-btn"
              className="btn-accent px-8 py-4 rounded-xl text-base inline-flex items-center gap-2 justify-center"
            >
              Get a Free Quote <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+16472422860"
              data-testid="cta-call-btn"
              className="px-8 py-4 rounded-xl text-base border-2 border-white text-white font-semibold hover:bg-white hover:text-[#3a5c2e] transition-all inline-flex items-center gap-2 justify-center"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <Phone size={18} /> Call 647.242.2860
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
